import React from 'react';
import { X, Crown, Sparkles, Clock, CreditCard, ChevronRight, AlertCircle } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// BookingGateModal — réplica simplificada del `OrchestratedSlotSheet` Flutter.
//
// Se muestra cuando `validate_schedule_include_consultation` devuelve
// `success: false`. Según las flags del backend (`without_membership`,
// `without_inscription`, `without_minutes`, `preferential_requires_membership`,
// `membership_ends_before`) renderiza el conjunto correcto de opciones:
//
//   1. withoutMembership / withoutInscription / preferentialRequiresMembership:
//      → "Adquirir membresía" + "Pagar como consulta extra"
//   2. withoutMinutes (membresía activa pero sin minutos):
//      → "Comprar minutos" + "Pagar como consulta extra"
//   3. membershipEndsBefore:
//      → "Renovar membresía" + "Pagar como consulta extra"
//
// El diseño preserva los tokens Daisies (purple primary, rounded-card,
// shadow-elevated) y muestra el resumen del horario seleccionado arriba.
// ─────────────────────────────────────────────────────────────────────────────

const formatHourLabel = (slot) => {
  if (!slot) return '';
  const start = slot.hourStart || slot.init || slot.hour || '';
  const stop = slot.hourStop || slot.end || '';
  if (start && stop) return `${start} – ${stop}`;
  return start || stop || '';
};

const formatDoctorLabel = (slot) => {
  const d = slot?.doctor || slot?.professional_ids?.[0];
  if (!d) return '';
  return `${d.name || ''} ${d.last_name || ''}`.trim();
};

const BookingGateModal = ({ slot, validation, gateKind, onChooseMembership, onChoosePayExtra, onClose }) => {
  if (!slot || !validation || !gateKind) return null;

  // gateKind viene EXPLÍCITO desde App.js para evitar interpretaciones erróneas
  // de respuestas ambiguas del backend (success: false sin flag específico).
  let title, subtitle, primaryLabel, primaryDesc, primaryIcon;
  if (gateKind === 'preferential') {
    title = 'Horario preferencial';
    subtitle = 'Este horario solo está disponible para nuestras miembros. Adquiere tu membresía o paga la consulta directamente.';
    primaryLabel = 'Adquirir membresía';
    primaryDesc = 'Acceso preferencial y sin pago por consulta';
    primaryIcon = <Crown size={18} />;
  } else if (gateKind === 'membership_ends_before') {
    title = 'Tu membresía vence pronto';
    subtitle = 'La cita que elegiste es después de tu fecha de corte. Renueva tu membresía o paga esta consulta directamente.';
    primaryLabel = 'Renovar membresía';
    primaryDesc = 'Mantén tu acceso sin pagos por consulta';
    primaryIcon = <Crown size={18} />;
  } else if (gateKind === 'without_minutes') {
    title = 'Te quedaste sin minutos';
    subtitle = 'Tu membresía está activa, pero ya usaste todas las consultas incluidas. Compra minutos o paga esta cita directamente.';
    primaryLabel = 'Comprar minutos';
    primaryDesc = 'Reactiva tus consultas incluidas';
    primaryIcon = <Sparkles size={18} />;
  } else {
    // gateKind === 'without_membership' (default explícito).
    title = 'Necesitas una membresía';
    subtitle = 'Para agendar esta consulta como incluida necesitas una membresía activa. También puedes pagar esta consulta de forma individual.';
    primaryLabel = 'Adquirir membresía';
    primaryDesc = 'Consultas incluidas y precios preferenciales';
    primaryIcon = <Crown size={18} />;
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-end md:items-center justify-center p-0 md:p-4 overflow-y-auto animate-fadeIn">
      <div className="bg-white rounded-t-card md:rounded-card shadow-elevated w-full md:max-w-xl max-h-[95vh] md:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative px-6 md:px-8 pt-6 md:pt-7 pb-5 bg-brand-50">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 text-text-secondary hover:text-text-primary transition-colors"
            aria-label="Cerrar"
          >
            <X size={24} />
          </button>
          <div className="flex items-start gap-4 pr-10">
            <div className="p-3 bg-daisies-50 text-daisies rounded-input flex-shrink-0">
              <AlertCircle size={26} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl md:text-2xl font-bold text-text-primary leading-tight">{title}</h3>
              <p className="text-sm md:text-base text-text-secondary mt-2 leading-relaxed">{subtitle}</p>
            </div>
          </div>
        </div>

        {/* Resumen del horario */}
        <div className="px-6 md:px-8 py-4 bg-surface/60 border-b border-brand-100">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white rounded-input">
              <Clock size={18} className="text-daisies" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="ds-caption text-text-secondary uppercase tracking-wider">Horario seleccionado</p>
              <p className="text-base font-semibold text-text-primary truncate">
                {formatHourLabel(slot)}
                {formatDoctorLabel(slot) ? ` · ${formatDoctorLabel(slot)}` : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Opciones — botones grandes con jerarquía clara */}
        <div className="px-6 md:px-8 py-6 space-y-3 overflow-y-auto">
          <button
            onClick={onChooseMembership}
            className="press-scale-card w-full text-left p-5 md:p-6 rounded-card bg-daisies text-white transition-all flex items-center gap-4"
          >
            <div className="p-3 bg-white/20 rounded-input flex-shrink-0">{primaryIcon}</div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-lg md:text-xl leading-tight">{primaryLabel}</div>
              <div className="text-sm text-white/85 mt-1">{primaryDesc}</div>
            </div>
            <ChevronRight size={24} className="text-white flex-shrink-0" />
          </button>

          <button
            onClick={onChoosePayExtra}
            className="press-scale-card w-full text-left p-5 md:p-6 rounded-card bg-warm-canvas hover:bg-daisies-50 transition-all flex items-center gap-4"
          >
            <div className="p-3 bg-daisies-50 text-daisies rounded-input flex-shrink-0"><CreditCard size={22} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-lg md:text-xl leading-tight text-text-primary">Pagar consulta extra</div>
              <div className="text-sm text-text-secondary mt-1">Pago único, sin membresía</div>
            </div>
            <ChevronRight size={24} className="text-daisies flex-shrink-0" />
          </button>
        </div>

        {/* Cancelar */}
        <div className="px-6 md:px-8 pb-6 pt-1">
          <button
            onClick={onClose}
            className="press-scale w-full py-3.5 text-text-secondary text-sm md:text-base font-semibold rounded-full hover:bg-surface transition-all"
          >
            Elegir otro horario
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingGateModal;
